/**
 * LE DÉTAIL D'UNE ALARME (ROUTE 12) : UNE LECTURE QUI A UN EFFET.
 * ============================================================================
 *
 * ⚠️ Lire la route 12 (le détail d'une alarme) CLÔT une alarme revenue à la
 * normale et non vue, en nommant le lecteur (D71, matrice geste 39
 * `episode_vu`). Donc :
 *  • une seule méthode appelle la route, `ouvrirLeDetail`, et seule la page
 *    du détail l'appelle, à son entrée, sur une navigation de la personne :
 *    jamais de préchargement, jamais au survol, jamais depuis une liste (garde
 *    `gardes.statiques.spec.ts`) ;
 *  • une lecture par ouverture : la page ne relit pas d'elle-même quand la
 *    session change, elle vide.
 *
 * Tenu par session (personne et entreprise) : une réponse arrivée pour une
 * autre session, ou pour une autre ouverture, est jetée sans toucher à
 * l'état, en succès comme en échec.
 *
 * Le détail servi est confronté à l'adresse : une autre alarme que celle
 * demandée, ou une alarme d'une autre surveillance que celle de l'adresse (un
 * lien modifié ou collé), n'est jamais montrée sous l'en-tête de l'adresse.
 * C'est un refus dit par la phrase neutre, comme un objet hors de portée.
 */
import { defineStore } from "pinia"
import { ref } from "vue"
import { cleDeSession } from "@modules/alarmes-v2/stores/ouverture.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import {
    CODE_INDISPONIBLE,
    ErreurAlarmesV2,
    versErreurAlarmesV2,
} from "@modules/alarmes-v2/services/appel"
import type {
    AlarmesV2Acquittement,
    AlarmesV2DetailAlarme,
    AlarmesV2EtatAlarme,
} from "@modules/alarmes-v2/types/alarmes-v2"

/** Les états d'une alarme que la liste et le détail servent. */
const ETATS_DE_LA_LISTE: readonly AlarmesV2EtatAlarme[] = [
    "declenchee_non_prise_en_compte",
    "declenchee_prise_en_compte",
    "declenchee_pour_information",
    "retour_a_la_normale_non_vu",
    "close",
]

/** Où en est la lecture du détail. */
export type StatutDetail = "attente" | "chargement" | "pret" | "erreur"

/** Le plus de lignes de journal que la façade sert en une fois. */
export const LIMITE_JOURNAL = 500

/** Le détail servi n'est pas celui de l'adresse (refus, phrase neutre). */
export const CODE_HORS_ADRESSE = "alarme_hors_adresse"

export const useDetailAlarmeStore = defineStore("alarmesV2DetailAlarme", () => {
    /** Le détail lu, `null` sinon. */
    const detail = ref<AlarmesV2DetailAlarme | null>(null)

    const statut = ref<StatutDetail>("attente")
    const erreur = ref<ErreurAlarmesV2 | null>(null)

    /** La session et l'alarme lues, `""` sinon. */
    const cleLue = ref("")

    let lectureCourante = 0

    /**
     * Ouvre le détail d'une alarme : UNE lecture de la route 12. Ne rejette
     * jamais. À n'appeler que depuis la page du détail, à son entrée.
     * @param idAlarme - `_id` de l'alarme ouverte par la personne
     * @param idInstance - `_id` de la surveillance de l'adresse : l'alarme servie doit en être
     */
    async function ouvrirLeDetail(
        idAlarme: string,
        idInstance: string
    ): Promise<void> {
        const session = cleDeSession()
        if (!session || !idAlarme || !idInstance) {
            vider()
            return
        }
        const cle = `${session}|${idAlarme}`
        lectureCourante += 1
        const numero = lectureCourante
        detail.value = null
        erreur.value = null
        statut.value = "chargement"
        const courante = () =>
            numero === lectureCourante &&
            `${cleDeSession()}|${idAlarme}` === cle
        try {
            const lu = await serviceAlarmesV2.alarmesV2LireAlarme(
                idAlarme,
                LIMITE_JOURNAL
            )
            if (!courante()) return
            const occurrence = lu?.occurrence
            if (
                !occurrence ||
                typeof occurrence !== "object" ||
                !occurrence._id
            )
                throw new ErreurAlarmesV2({
                    code: CODE_INDISPONIBLE,
                    statut: null,
                })
            if (
                occurrence._id !== idAlarme ||
                occurrence._id_instance !== idInstance
            )
                throw new ErreurAlarmesV2({
                    code: CODE_HORS_ADRESSE,
                    statut: null,
                })
            detail.value = {
                occurrence,
                journal: Array.isArray(lu.journal) ? lu.journal : [],
                livraisons: Array.isArray(lu.livraisons) ? lu.livraisons : [],
                curseur_suivant: lu.curseur_suivant ?? null,
                personnes: lu.personnes ?? {},
            }
            statut.value = "pret"
        } catch (e) {
            if (!courante()) return
            detail.value = null
            erreur.value =
                e instanceof ErreurAlarmesV2 ? e : versErreurAlarmesV2(e)
            statut.value = "erreur"
        }
        cleLue.value = cle
    }

    /**
     * Pose l'acquittement que le SERVEUR a rendu (route 7, ou la relecture de
     * la liste) sur le détail montré, sans relire la route 12 : jamais une
     * écriture supposée. Ignoré si le détail montré n'est pas cette alarme de
     * la session courante.
     * @param idAlarme - `_id` de l'alarme acquittée
     * @param servi - l'état et le premier acquitteur, tels que servis
     */
    function appliquerAcquittement(
        idAlarme: string,
        servi: AlarmesV2Acquittement
    ): void {
        const d = detail.value
        if (
            !d?.occurrence ||
            d.occurrence._id !== idAlarme ||
            cleLue.value !== `${cleDeSession()}|${idAlarme}`
        )
            return
        const occurrence = { ...d.occurrence }
        // La route 7 nomme l'état dans son propre vocabulaire (`prise_en_compte`,
        // spec de la façade) : seul un état que la liste sert est repris ;
        // l'acquitteur, lui, est toujours posé. La relecture donne le reste.
        const etat = ETATS_DE_LA_LISTE.find((e) => e === servi.etat)
        if (etat) occurrence.etat = etat
        if (servi.prise_en_compte !== undefined)
            occurrence.prise_en_compte = servi.prise_en_compte
        detail.value = {
            ...d,
            occurrence,
            personnes: { ...(d.personnes ?? {}), ...(servi.personnes ?? {}) },
        }
    }

    /** Vide le détail ; toute lecture en vol est jetée. */
    function vider(): void {
        lectureCourante += 1
        detail.value = null
        erreur.value = null
        statut.value = "attente"
        cleLue.value = ""
    }

    return {
        detail,
        statut,
        erreur,
        cleLue,
        ouvrirLeDetail,
        appliquerAcquittement,
        vider,
    }
})
